import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Login from './Login';
import './ProgressiveSignupPrompt.css';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

const EXCLUDED_PATH_PREFIXES = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/terms',
  '/privacy',
];

const isExcludedPath = (pathname) => {
  return EXCLUDED_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const AuthIdleLogoutGate = () => {
  const { isLoggedIn, isLoading, logout } = useAuth();
  const location = useLocation();
  const [isGateOpen, setIsGateOpen] = useState(false);
  const logoutInProgressRef = useRef(false);
  const timerRef = useRef(null);
  const dialogRef = useRef(null);

  const shouldMonitorIdle = useMemo(() => {
    return !isExcludedPath(location.pathname);
  }, [location.pathname]);

  const clearIdleTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleIdleTimeout = useCallback(async () => {
    if (logoutInProgressRef.current || !isLoggedIn || isLoading || !shouldMonitorIdle) {
      return;
    }

    logoutInProgressRef.current = true;
    clearIdleTimer();

    try {
      await logout();
    } finally {
      setIsGateOpen(true);
      logoutInProgressRef.current = false;
    }
  }, [clearIdleTimer, isLoading, isLoggedIn, logout, shouldMonitorIdle]);

  const resetIdleTimer = useCallback(() => {
    if (!isLoggedIn || isLoading || isGateOpen || !shouldMonitorIdle) {
      clearIdleTimer();
      return;
    }

    clearIdleTimer();
    timerRef.current = window.setTimeout(handleIdleTimeout, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, handleIdleTimeout, isGateOpen, isLoading, isLoggedIn, shouldMonitorIdle]);

  const handleLoginSuccess = useCallback(() => {
    setIsGateOpen(false);
    resetIdleTimer();
  }, [resetIdleTimer]);

  useEffect(() => {
    resetIdleTimer();
    return clearIdleTimer;
  }, [clearIdleTimer, location.pathname, resetIdleTimer]);

  useEffect(() => {
    if (!isLoggedIn || isLoading || isGateOpen || !shouldMonitorIdle) {
      return undefined;
    }

    const activityEvents = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetIdleTimer, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetIdleTimer);
      });
    };
  }, [isGateOpen, isLoading, isLoggedIn, resetIdleTimer, shouldMonitorIdle]);

  useEffect(() => {
    if (!isGateOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('auth-idle-gate-open');
    dialogRef.current?.focus();

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.classList.remove('auth-idle-gate-open');
    };
  }, [isGateOpen]);

  if (!isGateOpen) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="progressive-signup-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.section
          className="progressive-signup-dialog progressive-signup-page-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Session expired. Sign in again."
          tabIndex="-1"
          ref={dialogRef}
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 18, scale: 0.98 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          onClick={(event) => event.stopPropagation()}
        >
          <Login
            onLoginSuccess={handleLoginSuccess}
            redirectMessage="You were signed out after 30 minutes of inactivity. Sign in or continue with Google to resume."
          />
        </motion.section>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthIdleLogoutGate;
